export interface AccountStorageOwner {
    firmId: string;
    userId: string;
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const accountKeyBases = [
    'outlaw.user.firm', 'outlaw.portal.firm', 'outlaw.auth.firms',
    'outlaw.tasks.selectedCategories', 'outlaw.tasks.selectedUsers', 'outlaw.polling.refreshType',
    'outlaw.dashboard.feedWindow', 'outlaw.dashboard.feedFilters',
    'outlaw.dashboard.feedUsers', 'outlaw.dashboard.myHoursPeriod',
];

/** Enumerate only application keys whose name establishes this exact account. */
export function accountLocalStorageKeys(owner: AccountStorageOwner, storage: Storage = localStorage): string[] {
    // Fixed-width IDs make the historical hyphen-separated suffix unambiguous.
    if (!uuid.test(owner.firmId) || !uuid.test(owner.userId)) throw new Error('A complete account identity is required to clear its preferences.');
    const suffix = `-${owner.firmId}-${owner.userId}`;
    const replayKeys = new Set([
        `replay_visitId:${owner.firmId}:${owner.userId}`,
        `replay_visitLastActivity:${owner.firmId}:${owner.userId}`,
    ]);
    const accountKeys = new Set(accountKeyBases.flatMap(base => [base + suffix, base + suffix + ';']));
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (key && (replayKeys.has(key) || accountKeys.has(key))) keys.push(key);
    }
    return keys;
}

/**
 * Explicit account-localStorage reset. Call only after showing its shared-tab
 * effects. Does not touch sessionStorage, SDK locks, global/ambiguous legacy
 * keys, user-wide membership lists, or any IndexedDB database.
 * Other same-account tabs can recreate preferences after this operation.
 */
export function clearAccountLocalStorage(owner: AccountStorageOwner, storage: Storage = localStorage): number {
    const keys = accountLocalStorageKeys(owner, storage);
    for (const key of keys) {
        storage.removeItem(key); // Let failures reach the UI; never claim a partial clear succeeded.
        if (storage === window.localStorage) {
            // Browsers notify the OTHER documents automatically, not this one.
            window.dispatchEvent(new StorageEvent('storage', {key, newValue: null, storageArea: storage}));
            window.dispatchEvent(new CustomEvent('storageKey', {detail: {key, value: ''}}));
        }
    }
    return keys.length;
}

export const ACCOUNT_STORAGE_CLEAR_EFFECTS = 'This removes the account entries currently used for task filters, dashboard settings, polling refresh settings, cached firm details, firm membership, and active recording visit information for this firm and user from this browser. Other browser entries are left in place. Other tabs signed in as the same user in this firm share the removed entries: their settings may reset, and an open tab may recreate them. Tabs using a different firm or user are not cleared. This does not remove queued changes, saved drafts, recordings, or backups in IndexedDB, and it does not sign out other tabs. Close other tabs for this account first if you want the reset to remain in effect.';
